import { encodeFunctionData, decodeEventLog } from "viem";
import { isZeroDevConnector } from "@dynamic-labs/ethereum-aa";
import { getContractAddress, getContractABI } from "./utils/contracts";

const VITE_SERVER_URL = import.meta.env.VITE_SERVER_URL;

export const createProjectWithEntrypoint = async ({
  primaryWallet,
  projectData,
  fundingWallet,
  nodesToReserve,
  escrowAmount,
  slot,
  nodeIds,
  nodePrices,
}: {
  primaryWallet: any;
  projectData: any;
  fundingWallet: string;
  nodesToReserve: number;
  escrowAmount: number;
  slot: { last: boolean; next: boolean };
  nodeIds: string[];
  nodePrices: number[];
}) => {
  if (!primaryWallet) {
    throw new Error("No wallet connected");
  }

  try {
    const { connector, address } = primaryWallet;
    const ethAddress = address as `0x${string}`;

    if (!isZeroDevConnector(connector)) {
      throw new Error("Not a ZeroDev connector");
    }

    // Initialize ZeroDev connector
    await connector.getNetwork();
    await connector.getAddress();
    await connector.getAccountAbstractionProvider();

    const kernelClient = connector.getAccountAbstractionProvider({
      withSponsorship: true,
    });

    if (!kernelClient || !kernelClient.account) {
      throw new Error("Failed to initialize kernel client");
    }

    // Load contract data
    const [usdcABI, earthfastEntrypointABI] = await Promise.all([
      getContractABI("USDC"),
      getContractABI("EarthfastEntrypoint"),
    ]);

    const [USDC_ADDRESS, ENTRYPOINT_ADDRESS, PROJECTS_ADDRESS] =
      await Promise.all([
        getContractAddress("USDC"),
        getContractAddress("EarthfastEntrypoint"),
        getContractAddress("EarthfastProjects"),
      ]);

    // Encode USDC approval
    const usdcApprovalData = encodeFunctionData({
      abi: usdcABI,
      functionName: "approve",
      args: [PROJECTS_ADDRESS, escrowAmount],
    });

    // TODO: need to parameterize content, checksum, metadata
    // convert projectData to struct EarthfastCreateProjectData
    const projectDataStruct = {
      owner: fundingWallet,
      name: projectData.name,
      email: projectData.email,
      content: "projectData.content",
      checksum:
        "0x0000000000000000000000000000000000000000000000000000000000000000",
      metadata: "projectData.metadata",
    };

    // Encode deploySiteWithApproval
    const deploySiteData = encodeFunctionData({
      abi: earthfastEntrypointABI,
      functionName: "deploySiteWithApproval",
      args: [
        projectDataStruct,
        fundingWallet,
        nodeIds,
        nodePrices,
        nodesToReserve,
        escrowAmount,
        slot,
      ],
    });

    // Send batched user operation
    const opHash = await kernelClient.sendUserOperation({
      callData: await kernelClient.account.encodeCalls([
        {
          to: USDC_ADDRESS,
          value: BigInt(0),
          data: usdcApprovalData,
        },
        {
          to: ENTRYPOINT_ADDRESS,
          value: BigInt(0),
          data: deploySiteData,
        },
      ]),
    });

    // get projectId from user operation logs
    const projectId = await getProjectIdFromUserOperation(
      opHash,
      primaryWallet,
    );

    // write newly created project to db
    await fetch(`${VITE_SERVER_URL}/project/client`, {
      credentials: "include",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        projectId,
        projectOwner: fundingWallet,
        projectData,
        transactionHash: opHash,
      }),
    });

    return projectId;
  } catch (err) {
    console.error("Error sending user operation:", err);
    throw err instanceof Error
      ? err
      : new Error("Failed to send user operation");
  }
};

export const getProjectIdFromUserOperation = async (
  opHash: string,
  primaryWallet: any,
) => {
  try {
    // Create a public client to interact with the blockchain
    const client = await getKernelClient(primaryWallet);

    // Wait for the user operation to be mined and get the transaction receipt
    const receipt = await client.waitForUserOperationReceipt({
      hash: opHash as `0x${string}`,
    });

    const transactionHash = receipt.receipt.transactionHash;

    // Load EarthfastEntrypoint ABI
    const earthfastEntrypointABI = await getContractABI("EarthfastEntrypoint");

    // Decode the logs using the contract ABI
    const decodedLogs = receipt.logs.map((log) => {
      try {
        return decodeEventLog({
          abi: earthfastEntrypointABI,
          data: log.data,
          topics: log.topics,
        });
      } catch (e) {
        return null;
      }
    });

    // Find the SiteDeployed event
    const siteDeployedEvent = decodedLogs.find(
      (log) => log && log.eventName === "SiteDeployed",
    ) as { eventName: string; args: { projectId: `0x${string}` } } | null;

    if (!siteDeployedEvent || !siteDeployedEvent.args) {
      throw new Error("SiteDeployed event not found in transaction logs");
    }

    // The ProjectId is the first indexed parameter in the event
    const projectId = siteDeployedEvent.args.projectId;
    return projectId;
  } catch (err) {
    console.error("Error getting project ID from user operation:", err);
    throw err instanceof Error
      ? err
      : new Error("Failed to get project ID from user operation");
  }
};

export const getKernelClient = async (primaryWallet: any) => {
  if (!primaryWallet) {
    throw new Error("No wallet connected");
  }

  const { connector, address } = primaryWallet;

  if (!isZeroDevConnector(connector)) {
    throw new Error("Not a ZeroDev connector");
  }

  // Initialize ZeroDev connector
  await connector.getNetwork();
  await connector.getAddress();
  await connector.getAccountAbstractionProvider();

  const kernelClient = connector.getAccountAbstractionProvider({
    withSponsorship: true,
  });

  if (!kernelClient || !kernelClient.account) {
    throw new Error("Failed to initialize kernel client");
  }

  return kernelClient;
};
